/*
 * Copyright (c) 2019. Nedaware SL
 */
/**
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
import {Injectable} from '@angular/core';
import {ApiService} from "../../api/api.service";
import {InterfaceProviderService} from "./interface-provider.service";
import {Resource} from "../../api/resource";
import {TempUri, Uri} from "../../api/uri";
import {FilterAndData, FilterList} from "../../api/filter-list";
import {Paginated} from "../../api/collection-answer";
import {BasicEntityInterface} from "../basic-entity-interface/basic-entity-interface";
import {Observable, Subject} from "rxjs";
import {takeWhile} from "rxjs/operators";
import {IriSearchFilter} from "../filters/iri-search-filter";

/**
 * This service provides and caches the names for the elements inside the app.
 * However, it should be replaced for a general-purpose provider of the entities
 * in the future, through a complete entity provider.
 */
@Injectable({
    providedIn: 'root'
})
export class EntityNameService {
    /**
     * Saves a map of each IRI to the name
     */
    private _nameMap: Map<string, string> = new Map<string, string>();
    /**
     * Saves a map with each IRI which is currently loading and an Observable for it
     */
    private _loading: Map<string, Observable<void>> = new Map<string, Observable<void>>();

    constructor(private _api: ApiService, private _interfaceProvider: InterfaceProviderService) {
    }

    /**
     * Clear the cache!
     */
    public clear() {
        this._nameMap.clear();
    }

    /**
     * Get the name for the given IRI, if saved in the system
     * @param iri - IRI of the element to obtain the name
     * @param updateCache - Update the cache requesting the network even if the element is present
     */
    public get(iri: Uri, updateCache = true): Promise<string> {
        if (iri instanceof TempUri) {
            return new Promise<string>((resolve, reject) => reject('Temporal URI: ' + iri));
        }
        return new Promise<string>((resolve, reject) => this._getExecutor(iri, updateCache, resolve, reject));
    }

    /**
     * Updates a bunch of iris (tries to use a filter to reduce network connections)
     * and resolves the promise when all of them have been updated.
     * @param iris - The iris to update the names for
     * @param onlyNotPresent - If true, only the iris which are not cached already will be requested
     */
    public bulkCacheUpdate(iris: Uri[], onlyNotPresent = false): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            iris = iris.filter(iri => !(iri instanceof TempUri)); // Eliminar temporales
            if (onlyNotPresent) {
                iris = iris.filter(iri => this._nameMap.has(iri.toString()) || this._loading.has(iri.toString()));
            }
            if (iris.length === 0) {
                resolve();
                return;
            }

            const strIris: string[] = iris.map(iri => iri.toString());

            const manager = this._interfaceProvider.managerForModel(iris[0].model);
            const interf = manager.loader.entityInterface;

            const subject = new Subject<void>();
            for (const iri of strIris) {
                if (!this._loading.has(iri)) {
                    this._loading.set(iri, subject);
                }
            }

            // Has caching, no bulk needed
            if (interf.managerCaching) {
                let received = 0;
                manager.all
                    .pipe(takeWhile(_ => received < strIris.length))
                    .subscribe(entities => {
                        for (const entity of entities) {
                            this._nameMap.set(entity.iri.toString(), interf.getName(entity));
                            if (strIris.includes(entity.iri.toString())) {
                                this._loading.delete(entity.iri.toString());
                                received++;
                            }
                        }
                        if (received >= strIris.length) {
                            subject.complete();
                            resolve();
                        }
                    });
            } else {
                if (!interf.acceptsBulkIriSearch) {
                    reject(`The interface for the endpoint "${interf.endpoint}" does not accept bulk iri search and it is not cached by the manager.`);
                }

                const filters: FilterList = [new FilterAndData(IriSearchFilter, iris)];
                manager.loader.findAndFollow(filters)
                    .subscribe(
                        paginated => this._bulkReception(interf, paginated, subject),
                        error => reject(error),
                        () => resolve()
                    );
            }
        });
    }

    private _bulkReception(interf: BasicEntityInterface<Resource>, paginated: Paginated<Resource>, onCompleteSubject: Subject<void>) {
        for (const entity of paginated.member) {
            this._nameMap.set(entity.iri.toString(), interf.getName(entity));
            this._loading.delete(entity.iri.toString());
        }
        if (paginated.isLastPage()) {
            onCompleteSubject.next();
            onCompleteSubject.complete();
        }
    }

    /**
     * Save the name given a model or an Uri and a name
     * @param iri - The iri to save the name for
     * @param name - The name
     */
    public set(iri: Uri, name: string) {
        this._nameMap.set(iri.toString(), name);
    }

    private _getExecutor(
        iri: Uri,
        updateCache: boolean,
        resolve: (value?: PromiseLike<string> | string) => void,
        reject: (reason?: any) => void
    ) {
        const alreadyCached = this._nameMap.has(iri.toString());
        if (alreadyCached) {
            resolve(this._nameMap.get(iri.toString()));
        }

        const loading = this._loading.get(iri.toString());
        if (loading) {
            loading.subscribe(undefined, undefined,
                () => {
                    resolve(this._nameMap.get(iri.toString()));
                });
            return;
        }

        if (!alreadyCached || updateCache) {
            const manager = this._interfaceProvider.managerForModel(iri.model);
            if (manager.hasCached(iri.toString())) {
                const name = manager.loader.entityInterface.getName(manager.getFromCache(iri.toString()));
                this._nameMap.set(iri.toString(), name);
                if (!alreadyCached) {
                    resolve(name);
                }
            } else {
                const subject = new Subject<void>();
                this._loading.set(iri.toString(), subject);
                manager.get(iri.toString())
                    .then(entity => {
                        const name = manager.loader.entityInterface.getName(entity);
                        this._nameMap.set(iri.toString(), name);
                        if (!alreadyCached) {
                            resolve(name);
                        }
                        // Notify the waiting gets
                        this._loading.delete(iri.toString());
                        subject.next();
                        subject.complete();
                    })
                    .catch(error => reject(error));
            }
        }
    }
}
